import { useCallback, useEffect, useState } from 'react';
import type {
  BankAccount,
  BillResult,
  ContactSuggestion,
  InboxFile,
  InboxSetup,
  ParseMatchResult,
  TxnMatch,
} from '../api/client';
import { api } from '../api/client';

// ── Helpers ──────────────────────────────────────────────────

function fmt(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function fmtDate(iso: string): string {
  if (!iso) return '';
  return iso.split('T')[0] ?? iso;
}

const CONF_COLOR: Record<string, string> = { HIGH: '#080', MEDIUM: '#a60', LOW: '#888' };

// ── Sub-components ───────────────────────────────────────────

function SetupBanner({ setup }: { setup: InboxSetup | null }) {
  if (!setup) return null;
  const issues: string[] = [];
  if (!setup.xeroConnected) issues.push('Xero not connected');
  if (!setup.anthropicKey) issues.push('ANTHROPIC_API_KEY not set');

  if (issues.length === 0) {
    return (
      <div
        style={{
          background: '#f0fff0',
          border: '1px solid #cfc',
          borderRadius: 4,
          padding: '6px 12px',
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        ✓ {setup.xeroOrgName || 'Connected'} — Anthropic key set
        {setup.archiveFolderId ? '' : ' — ⚠ No "Archive" folder in Xero Files'}
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#fff8f0',
        border: '1px solid #fca',
        borderRadius: 4,
        padding: '6px 12px',
        marginBottom: 12,
        fontSize: 13,
      }}
    >
      ⚠ {issues.join(' · ')}
    </div>
  );
}

function ContactConfirmDialog({
  result,
  onConfirm,
  onCancel,
}: {
  result: BillResult;
  onConfirm: (contactId: string | undefined) => void;
  onCancel: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 400, width: '90%' }}
      >
        <h3 style={{ margin: '0 0 12px' }}>Confirm contact</h3>
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          Vendor: <strong>{result.vendorName}</strong>
        </p>
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#666' }}>Similar contacts found:</p>
        {result.suggestions?.map((s: ContactSuggestion) => (
          <button
            key={s.id}
            onClick={() => onConfirm(s.id)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '6px 10px',
              marginBottom: 6,
              border: '1px solid #ddd',
              borderRadius: 4,
              cursor: 'pointer',
              background: 'var(--bg2)',
            }}
          >
            {s.name}
          </button>
        ))}
        <button
          onClick={() => onConfirm(undefined)}
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '6px 10px',
            marginBottom: 6,
            border: '1px solid #88c',
            borderRadius: 4,
            cursor: 'pointer',
            background: 'var(--blue-lt)',
          }}
        >
          Create new contact: "{result.vendorName}"
        </button>
        <button
          onClick={onCancel}
          style={{
            marginTop: 8,
            fontSize: 12,
            color: '#888',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function MatchDialog({
  result,
  onConfirm,
  onCancel,
}: {
  result: ParseMatchResult;
  onConfirm: (match: TxnMatch) => void;
  onCancel: () => void;
}) {
  const matches = result.matches ?? [];
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 500, width: '90%' }}
      >
        <h3 style={{ margin: '0 0 8px' }}>Match to bank transaction</h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#666' }}>
          {result.vendor} · {result.amount} · {result.date}
        </p>
        {matches.length === 0 ? (
          <p style={{ color: '#888', fontSize: 14 }}>No matching transactions found.</p>
        ) : (
          matches.map((m) => (
            <button
              key={m.id}
              onClick={() => onConfirm(m)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '8px 10px',
                marginBottom: 8,
                border: '1px solid #ddd',
                borderRadius: 4,
                cursor: 'pointer',
                background: 'var(--bg2)',
              }}
            >
              <span
                style={{
                  color: CONF_COLOR[m.confidence] ?? '#888',
                  fontWeight: 600,
                  fontSize: 11,
                  marginRight: 8,
                }}
              >
                {m.confidence}
              </span>
              {m.dateStr} · ${m.amount.toFixed(2)} · {m.contact || m.type}
              {m.reference ? ` · ${m.reference}` : ''}
              {m.daysDiff !== null ? (
                <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({m.daysDiff}d)</span>
              ) : null}
            </button>
          ))
        )}
        <button
          onClick={onCancel}
          style={{
            marginTop: 8,
            fontSize: 12,
            color: '#888',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── File row ─────────────────────────────────────────────────

type FileStatus =
  | { type: 'idle' }
  | { type: 'busy'; action: string }
  | { type: 'done'; message: string; billUrl?: string }
  | { type: 'error'; message: string }
  | { type: 'contact_confirm'; billResult: BillResult }
  | { type: 'match_result'; matchResult: ParseMatchResult };

function FileRow({
  file,
  locked,
  selectedAccount,
  txnsLoaded,
  onUnlock,
}: {
  file: InboxFile;
  locked: boolean;
  selectedAccount: string | null;
  txnsLoaded: boolean;
  onUnlock: (fileId: string) => void;
}) {
  const [status, setStatus] = useState<FileStatus>({ type: 'idle' });

  const handleParse = async () => {
    setStatus({ type: 'busy', action: 'Parsing…' });
    try {
      const res = await api.inboxParse(file.id, file.name, file.mimeType);
      if (res.error) {
        setStatus({ type: 'error', message: res.error });
      } else {
        setStatus({ type: 'done', message: `Renamed → ${res.newName ?? '?'}` });
      }
    } catch (e) {
      setStatus({ type: 'error', message: (e as Error).message });
    }
  };

  const handleBill = async () => {
    setStatus({ type: 'busy', action: 'Creating bill…' });
    try {
      const res = await api.inboxCreateBill(file.id, file.name, file.mimeType);
      if (res.error) {
        setStatus({ type: 'error', message: res.error });
      } else if (res.needsContactConfirmation) {
        setStatus({ type: 'contact_confirm', billResult: res });
      } else {
        setStatus({
          type: 'done',
          message: 'Bill created',
          ...(res.billUrl ? { billUrl: res.billUrl } : {}),
        });
      }
    } catch (e) {
      setStatus({ type: 'error', message: (e as Error).message });
    }
  };

  const handleContactConfirm = async (contactId: string | undefined) => {
    if (status.type !== 'contact_confirm') return;
    const br = status.billResult;
    setStatus({ type: 'busy', action: 'Creating bill…' });
    try {
      const res = await api.inboxCreateBillWithContact({
        fileId: br.fileId ?? file.id,
        vendorName: br.vendorName ?? '',
        ...(contactId !== undefined ? { contactId } : {}),
        ...(br.parsedAmount !== undefined ? { amount: br.parsedAmount } : {}),
        ...(br.parsedDate !== undefined ? { date: br.parsedDate } : {}),
      });
      if (res.error) {
        setStatus({ type: 'error', message: res.error });
      } else {
        setStatus({
          type: 'done',
          message: 'Bill created',
          ...(res.billUrl ? { billUrl: res.billUrl } : {}),
        });
      }
    } catch (e) {
      setStatus({ type: 'error', message: (e as Error).message });
    }
  };

  const handleMatch = async () => {
    if (!selectedAccount) return;
    setStatus({ type: 'busy', action: 'Matching…' });
    try {
      const res = await api.inboxParseMatch(file.id, file.name, file.mimeType, selectedAccount);
      if (res.error) {
        setStatus({ type: 'error', message: res.error });
      } else {
        setStatus({ type: 'match_result', matchResult: res });
      }
    } catch (e) {
      setStatus({ type: 'error', message: (e as Error).message });
    }
  };

  const handleConfirmMatch = async (match: TxnMatch) => {
    setStatus({ type: 'busy', action: 'Confirming…' });
    try {
      await api.inboxConfirmMatch(file.id, match.id, match.objectGroup);
      setStatus({
        type: 'done',
        message: `Matched to ${match.contact || match.type} (${match.dateStr})`,
      });
    } catch (e) {
      setStatus({ type: 'error', message: (e as Error).message });
    }
  };

  const isLocked = locked || status.type === 'done';
  const isBusy = status.type === 'busy';

  return (
    <>
      <tr style={{ opacity: isLocked ? 0.5 : 1 }}>
        <td style={{ padding: '6px 8px', fontSize: 13, wordBreak: 'break-all' }}>
          {file.name}
          {isLocked && !isBusy && (
            <button
              onClick={() => {
                onUnlock(file.id);
                setStatus({ type: 'idle' });
              }}
              title="Unlock"
              style={{
                marginLeft: 6,
                fontSize: 10,
                color: '#888',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              ↺
            </button>
          )}
        </td>
        <td style={{ padding: '6px 8px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
          {fmt(file.size)}
        </td>
        <td style={{ padding: '6px 8px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
          {fmtDate(file.createdDate)}
        </td>
        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
          {isBusy ? (
            <span style={{ fontSize: 12, color: '#888' }}>{status.action}</span>
          ) : status.type === 'done' ? (
            <span style={{ fontSize: 12, color: '#080' }}>
              ✓ {status.message}
              {status.billUrl && (
                <a href={status.billUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>
                  Open in Xero
                </a>
              )}
            </span>
          ) : status.type === 'error' ? (
            <span style={{ fontSize: 12, color: '#c00' }} title={status.message}>
              ✗ {status.message.slice(0, 60)}
              <button
                onClick={() => setStatus({ type: 'idle' })}
                style={{
                  marginLeft: 4,
                  fontSize: 10,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#888',
                }}
              >
                retry
              </button>
            </span>
          ) : !isLocked ? (
            <>
              <Btn onClick={() => void handleParse()} disabled={isBusy}>
                Parse
              </Btn>
              <Btn onClick={() => void handleBill()} disabled={isBusy} style={{ marginLeft: 4 }}>
                Bill
              </Btn>
              <Btn
                onClick={() => void handleMatch()}
                disabled={isBusy || !selectedAccount || !txnsLoaded}
                title={
                  !selectedAccount
                    ? 'Select a bank account first'
                    : !txnsLoaded
                      ? 'Load transactions first'
                      : ''
                }
                style={{ marginLeft: 4 }}
              >
                Match
              </Btn>
            </>
          ) : null}
        </td>
      </tr>
      {status.type === 'contact_confirm' && (
        <ContactConfirmDialog
          result={status.billResult}
          onConfirm={(id) => void handleContactConfirm(id)}
          onCancel={() => setStatus({ type: 'idle' })}
        />
      )}
      {status.type === 'match_result' && (
        <MatchDialog
          result={status.matchResult}
          onConfirm={(m) => void handleConfirmMatch(m)}
          onCancel={() => setStatus({ type: 'idle' })}
        />
      )}
    </>
  );
}

function Btn({
  onClick,
  disabled,
  children,
  title,
  style,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  title?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '2px 8px',
        fontSize: 12,
        border: '1px solid #ccc',
        borderRadius: 3,
        cursor: disabled ? 'default' : 'pointer',
        background: disabled ? '#f5f5f5' : '#fff',
        color: disabled ? '#aaa' : '#333',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── Main page ────────────────────────────────────────────────

export function InboxPage() {
  const [setup, setSetup] = useState<InboxSetup | null>(null);
  const [files, setFiles] = useState<InboxFile[]>([]);
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [txnsStatus, setTxnsStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [txnsMsg, setTxnsMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [setupRes, filesRes, lockedRes, accountsRes] = await Promise.all([
        api.inboxSetup(),
        api.inboxFiles(),
        api.inboxLocked(),
        api.inboxBankAccounts(),
      ]);
      setSetup(setupRes);
      setFiles(filesRes.files);
      setLockedIds(new Set(lockedRes.ids));
      setAccounts(accountsRes.accounts);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const handleLoadTxns = async () => {
    if (!selectedAccount) return;
    setTxnsStatus('loading');
    setTxnsMsg('Loading…');
    try {
      const res = await api.inboxLoadTxns(selectedAccount);
      setTxnsStatus('loaded');
      setTxnsMsg(
        `${res.count} transactions loaded (${res.txnCount} bank, ${res.paymentCount} payments)${res.paymentError ? ' — payments error: ' + res.paymentError : ''}`,
      );
    } catch (e) {
      setTxnsStatus('error');
      setTxnsMsg((e as Error).message);
    }
  };

  const handleUnlock = async (fileId: string) => {
    await api.inboxUnlock(fileId);
    setLockedIds((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
  };

  if (loading) return <p style={{ color: '#888', fontSize: 14 }}>Loading inbox…</p>;
  if (error)
    return (
      <p style={{ color: '#c00', fontSize: 14 }}>
        {error} <button onClick={() => void loadAll()}>Retry</button>
      </p>
    );

  return (
    <div className="max-w-[1100px] mx-auto px-7 py-7">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0 }}>Xero Inbox</h2>
        <button
          onClick={() => void loadAll()}
          style={{
            fontSize: 12,
            color: '#666',
            background: 'none',
            border: '1px solid #ddd',
            borderRadius: 3,
            padding: '2px 8px',
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>

      <SetupBanner setup={setup} />

      {/* Bank match setup */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
          padding: '8px 12px',
          background: 'var(--bg2)',
          borderRadius: 4,
          fontSize: 13,
        }}
      >
        <span>Bank account:</span>
        <select
          value={selectedAccount ?? ''}
          onChange={(e) => {
            setSelectedAccount(e.target.value || null);
            setTxnsStatus('idle');
            setTxnsMsg('');
          }}
          style={{ fontSize: 13, padding: '2px 6px' }}
        >
          <option value="">— select for bank match —</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.code ? ` (${a.code})` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={() => void handleLoadTxns()}
          disabled={!selectedAccount || txnsStatus === 'loading'}
          style={{
            fontSize: 12,
            padding: '2px 10px',
            border: '1px solid #ccc',
            borderRadius: 3,
            cursor: selectedAccount ? 'pointer' : 'default',
            background: '#fff',
          }}
        >
          Load transactions
        </button>
        {txnsMsg && (
          <span
            style={{
              fontSize: 12,
              color: txnsStatus === 'error' ? '#c00' : txnsStatus === 'loaded' ? '#080' : '#888',
            }}
          >
            {txnsMsg}
          </span>
        )}
      </div>

      {/* File table */}
      {files.length === 0 ? (
        <p style={{ color: '#888', fontSize: 14 }}>Inbox is empty.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 500 }}>
                Name
              </th>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 500 }}>
                Size
              </th>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 500 }}>
                Date
              </th>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#666', fontWeight: 500 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                locked={lockedIds.has(f.id)}
                selectedAccount={selectedAccount}
                txnsLoaded={txnsStatus === 'loaded'}
                onUnlock={(id) => void handleUnlock(id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
